import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Typography, Button, Grid, useTheme as useMuiTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TypeAnimation } from 'react-type-animation';
import { motion } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '../context/ThemeContext';
import CodeIcon from '@mui/icons-material/Code';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import TerminalIcon from '@mui/icons-material/Terminal';

// Styled components
const HeroContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'light' ? '#f8f9fa' : '#121212',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  paddingTop: '60px',
  paddingBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    height: 'auto',
    minHeight: '100vh',
    paddingTop: '20px',
    paddingBottom: '30px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'light' 
      ? 'radial-gradient(circle at center, rgba(106, 73, 219, 0.1) 0%, rgba(255, 255, 255, 0) 70%)'
      : 'radial-gradient(circle at center, rgba(106, 73, 219, 0.2) 0%, rgba(18, 18, 18, 0) 70%)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: '1.5rem',
  padding: '10px 24px',
  borderRadius: '30px',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  fontWeight: '500',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 10px 20px rgba(106, 73, 219, 0.3)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 25px rgba(106, 73, 219, 0.4)',
  },
}));

const ScrollIndicator = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  zIndex: 10,
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    bottom: 'auto',
    marginTop: '10px',
    width: '100%',
    left: 'auto',
    transform: 'none',
  },
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '380px',
  height: '380px',
  maxWidth: '100%',
  margin: '0 auto',
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    width: '320px',
    height: '320px',
    marginTop: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    width: '200px',
    height: '200px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const RotatingBorder = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '105%',
  height: '105%',
  borderRadius: '50%',
  border: '2px dashed',
  borderColor: theme.palette.primary.main,
  animation: 'rotate 15s linear infinite',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  // Top dot
  '&::before': {
    width: '16px',
    height: '16px',
    top: '-8px',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  // Right dot
  '&::after': {
    width: '12px',
    height: '12px',
    top: '50%',
    right: '-6px',
    transform: 'translateY(-50%)',
  },
  // Additional dots using pseudo selectors on child elements
  '& .dot-bottom': {
    content: '""',
    position: 'absolute',
    width: '14px',
    height: '14px',
    bottom: '-7px',
    left: '50%',
    transform: 'translateX(-50%)',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  '& .dot-left': {
    content: '""',
    position: 'absolute',
    width: '10px',
    height: '10px',
    top: '50%',
    left: '-5px',
    transform: 'translateY(-50%)',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  '@keyframes rotate': {
    '0%': {
      transform: 'translate(-50%, -50%) rotate(0deg)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) rotate(360deg)',
    },
  },
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
}));

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center 20%',
  borderRadius: '50%',
  transition: 'all 0.3s ease',
}));

const StatBox = styled(motion.div)(({ theme }) => ({
  padding: { xs: '15px 10px', md: '20px 15px' },
  textAlign: 'center',
  backgroundColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 30, 30, 0.9)',
  borderRadius: '20px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  height: { xs: '100px', md: '160px' },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.12)',
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
    borderStyle: 'solid',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 6px',
    height: '80px',
  },
}));

const Hero = () => {
  // We keep the useTheme hook but don't destructure from it since we're using MUI's theming
  useTheme();
  
  const muiTheme = useMuiTheme();
  
  // Check if device is mobile or small screen
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

  const stats = [
    { value: '2+', label: 'Years of Experience', icon: <WorkIcon sx={{ color: 'primary.main', fontSize: '2rem' }} /> },
    { value: '10+', label: 'Projects Completed', icon: <CodeIcon sx={{ color: 'primary.main', fontSize: '2rem' }} /> },
    { value: '7+', label: 'Technologies Explored', icon: <TerminalIcon sx={{ color: 'primary.main', fontSize: '2rem' }} /> },
    { value: '4.3', label: 'GPA / 5.0', icon: <SchoolIcon sx={{ color: 'primary.main', fontSize: '2rem' }} /> },
  ];

  return (
    <HeroContainer id="hero">
      <Container maxWidth="lg">
        <Grid container spacing={1} alignItems="center">
          {/* Left section with text */}
          <Grid item xs={12} md={7} sx={{ mb: { xs: 2, md: 0 } }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
                  fontWeight: 700,
                  mb: { xs: 0.5, md: 1.5 },
                  lineHeight: { xs: 1.2, md: 1.1 },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Hello I'm <Typography 
                  component="span" 
                  variant="h1" 
                  sx={{ 
                    color: 'primary.main',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    display: { xs: 'block', sm: 'inline' },
                  }}
                >
                  Sivagnanam Maheshwaran
                </Typography>
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: { xs: 1, md: 3 },
                  color: 'text.secondary',
                  fontWeight: 300,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <TypeAnimation
                  sequence={[
                    'Senior @ National University of Singapore',
                    1500,
                    'Computer Engineer',
                    1500,
                    'Building ML powered IoT systems',
                    1500,
                  ]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                  style={{ fontSize: { xs: '0.9rem', md: '1.15rem' } }}
                />
              </Typography>
            </motion.div>

            {/* Button container with proper responsive alignment */}
            <Box 
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <StyledButton
                  href="#about"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('about').scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ 
                    fontSize: { xs: '0.85rem', md: '1rem' },
                    padding: { xs: '8px 20px', md: '10px 24px' },
                    marginTop: { xs: '0.8rem', md: '1.5rem' }
                  }}
                >
                  Explore My Work
                </StyledButton>
              </motion.div>
            </Box>
          </Grid>
          
          {/* Right section with profile image */}
          <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <ProfileImageContainer>
                <RotatingBorder>
                  <span className="dot-bottom"></span>
                  <span className="dot-left"></span>
                </RotatingBorder>
                <ImageWrapper>
                  <StyledImage 
                    src="/Images/New_potrait.JPG" 
                    alt="Sivagnanam Maheshwaran"
                  />
                </ImageWrapper>
              </ProfileImageContainer>
            </motion.div>
          </Grid>
        </Grid>

        {/* Stats section - tighter margins on mobile */}
        <Box sx={{ mb: { xs: 2, md: 5 } }}>
          <Grid container spacing={1} sx={{ mt: { xs: 1, md: 3 } }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  width: '100%'
                }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 + (index * 0.1) }}
                    style={{ width: '70%' }}
                  >
                    <StatBox>
                      {/* Header Row - Icon and Value side by side */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: { xs: 0.5, md: 1 }
                      }}>
                        {React.cloneElement(stat.icon, { 
                          sx: { 
                            color: 'primary.main', 
                            fontSize: { xs: '1.2rem', md: '1.8rem' },
                            mr: 0.5
                          } 
                        })}
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main',
                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.2rem' },
                            lineHeight: 1,
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                      
                      {/* Label below */}
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.9rem' },
                          lineHeight: 1.2,
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </StatBox>
                  </motion.div>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ 
            opacity: { delay: 1, duration: 0.5 },
            y: { repeat: Infinity, duration: 1.5 }
          }}
          onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
          sx={{ 
            [muiTheme.breakpoints.down('sm')]: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 0.5,
              color: 'text.secondary',
              opacity: 0.7,
              fontSize: { xs: '0.7rem', md: '0.8rem' }
            }}
          >
            Scroll Down
          </Typography>
          <KeyboardArrowDownIcon 
            sx={{ 
              color: 'primary.main',
              fontSize: { xs: '1rem', md: '1.3rem' },
            }} 
          />
        </ScrollIndicator>
      </Container>
    </HeroContainer>
  );
};

export default Hero; 